import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from './config.js';
import { INTL_LOCALES, LOCALE_LABELS, SUPPORTED_LOCALES, normalizeLocale } from './config.js';

export interface UseLocaleResult {
  /** The active locale, always one of SUPPORTED_LOCALES. */
  locale: Locale;
  /** BCP-47 tag to hand to Intl / the formatters in @mypoz/shared. */
  intlLocale: string;
  /** Native-language label for the active locale. */
  label: string;
  locales: readonly Locale[];
  labels: Record<Locale, string>;
  setLocale: (next: Locale) => void;
}

/**
 * The one place the app reads or changes language. Persistence is handled by i18next's
 * localStorage detector; this hook adds the `<html lang>` mirroring i18next does not do.
 */
export function useLocale(): UseLocaleResult {
  const { i18n } = useTranslation();
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);

  // <html lang> drives screen-reader pronunciation, browser translation prompts, font fallback
  // for CJK, and CSS :lang() rules. Nothing sets it for us.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  return {
    locale,
    intlLocale: INTL_LOCALES[locale],
    label: LOCALE_LABELS[locale],
    locales: SUPPORTED_LOCALES,
    labels: LOCALE_LABELS,
    setLocale,
  };
}
